import PropTypes from 'prop-types';
import cn from 'classnames';

import './BrandTrackingHealthRow.scss';
import { severityClassName } from '../../helpers/brand_health_chart_helpers';
import TrackingHealthSparkline from './TrackingHealthSparkline';

const compactNumber = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

const buildDomainRollup = domains => ({
  errors: domains.filter(domain => domain.worstStatus === 'BROKEN' || domain.worstStatus === 'WARNING').length,
  healthy: domains.filter(domain => domain.worstStatus === 'OK').length,
  pending: domains.filter(domain => !['BROKEN', 'WARNING', 'OK'].includes(domain.worstStatus)).length
});

const BrandTrackingHealthRow = props => {
  const { brand, stats, onSelectBrand } = props;
  const domainCount = brand.domains.length;
  const rollup = buildDomainRollup(brand.domains);

  // All numbers live in one dot-separated group on the right (the `·` separators come from scss); volume
  // stats lead, the color-coded health rollup sits closest to the error sparkline.
  const segments = [];
  brand.mrr != null &&
    segments.push(
      <span key='mrr' className='brand-tracking-health-stat is-mrr'>
        ${compactNumber.format(brand.mrr)}/mo
      </span>
    );
  stats &&
    segments.push(
      <span key='orders' className='brand-tracking-health-stat'>
        {compactNumber.format(stats.ordersTracked)} orders tracked
      </span>,
      <span key='clicks' className='brand-tracking-health-stat'>
        {compactNumber.format(stats.clicksConfirmed)} clicks confirmed
      </span>,
      <span key='window' className='brand-tracking-health-stat'>
        {stats.windowDays}d
      </span>
    );
  rollup.errors > 0 &&
    segments.push(
      <span key='errors' className='brand-tracking-health-count is-broken'>
        {rollup.errors} {rollup.errors === 1 ? 'error' : 'errors'}
      </span>
    );
  rollup.healthy > 0 &&
    segments.push(
      <span key='healthy' className='brand-tracking-health-count is-ok'>
        {rollup.healthy} healthy
      </span>
    );
  rollup.pending > 0 &&
    segments.push(
      <span key='pending' className='brand-tracking-health-count is-pending'>
        {rollup.pending} pending
      </span>
    );

  return (
    <div className='brand-tracking-health-row' onClick={() => onSelectBrand(brand)}>
      <div className='brand-tracking-health-row-brand'>
        <div className='brand-tracking-health-row-heading'>
          <span className={cn('brand-tracking-health-status-dot', severityClassName(brand.worstStatus))} />
          <span className='brand-tracking-health-row-name'>{brand.name}</span>
          {brand.isOptedOut && <span className='brand-tracking-health-opted-out'>alerts off</span>}
        </div>
        <div className='brand-tracking-health-row-sub'>
          {brand.accountManager?.name || 'No account manager'} · {domainCount} domain{domainCount === 1 ? '' : 's'}
        </div>
      </div>

      <div className='brand-tracking-health-row-summary'>{segments}</div>

      <div className='brand-tracking-health-row-spark'>
        <TrackingHealthSparkline values={stats?.errorSparkline} />
      </div>
    </div>
  );
};

BrandTrackingHealthRow.propTypes = {
  brand: PropTypes.object.isRequired,
  stats: PropTypes.object,
  onSelectBrand: PropTypes.func.isRequired
};

export default BrandTrackingHealthRow;
